// src/services/authService.ts
import axios from 'axios';

// Use relative URL in development to work with proxy
const API_URL = process.env.NODE_ENV === 'production' 
  ? (process.env.REACT_APP_API_URL || 'https://api.prepstop.com')
  : ''; // Empty string means use proxy in development

// Shared axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // IMPORTANT: needed so browser sends/receives cookies for admin session
  withCredentials: true,
});

// Add token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export const authService = {
  // AdminJS: Send code to email for admin login
  sendAdminCodeLogin: async (email: string) => {
    const trimmedEmail = email.trim();
    const response = await axiosInstance.post('/admin-auth/login', { email: trimmedEmail });
    return response.data;
  },

  // AdminJS: Verify code and login for admin
  verifyAdminCodeLogin: async (email: string, code: string | number) => {
    const trimmedEmail = email.trim();
    const numericCode = Number(code);

    if (!Number.isInteger(numericCode)) {
      throw new Error('Invalid code format');
    }

    const response = await axiosInstance.post('/admin-auth/verify', {
      email: trimmedEmail,
      code: numericCode,
    });

    // Backend returns { user, token }, token is optional but useful for API calls
    if (response.data?.token) {
      localStorage.setItem('authToken', response.data.token);
    }

    return response.data; // { user, token }
  },

  // Legacy password login (if needed)
  login: async (email: string, password: string) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    if (response.data?.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    return response.data;
  },

  // Send code to email for login (supports both user and patient)
  sendCodeLogin: async (email: string, userType: 'user' | 'patient' = 'patient') => {
    const trimmedEmail = email.trim();
    const response = await axiosInstance.post('/auth/code-login', { 
      email: trimmedEmail,
      userType: userType 
    });
    return response.data;
  },

  // Verify code and login (supports both user and patient)
  verifyCodeLogin: async (email: string, code: string | number, userType: 'user' | 'patient' = 'patient') => {
    const numericCode = Number(code);
    const response = await axiosInstance.post('/auth/code-login/verify', {
      email: email.trim(),
      code: numericCode,
      userType: userType
    });

    return response.data; // { user, token, userType }
  },

  register: async (email: string, password: string, firstName: string, lastName: string) => {
    const response = await axiosInstance.post('/auth/register', {
      email,
      password,
      first_name: firstName,
      last_name: lastName,
    });
    return response.data;
  },

  // Register user with basic info (new flow)
  registerWithBasicInfo: async (data: {
    firstName: string;
    lastName: string;
    dob: string;
    email: string;
    phone: string;
    state: string;
    zip: string;
  }) => {
    const response = await axiosInstance.post('/user/register', data);
    return response.data;
  },

  // Resend OTP code
  resendOtp: async (email: string) => {
    const response = await axiosInstance.post('/auth/resend-otp', { email: email.trim() });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await axiosInstance.post('/auth/forgot-password', { email: email.trim() });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await axiosInstance.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('intakeBlockAFormData');
  },
};

export default axiosInstance;
